import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Scale, Shield, AlertTriangle, CheckCircle, Mail, Phone, MapPin } from 'lucide-react';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Terms of Service"
        description="Terms and conditions governing your use of CTC Waterworks services."
        canonicalPath="/terms-of-service"
        noIndex={true}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-slate-50 via-ctc-silver-light to-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center space-x-3 bg-ctc-blue/10 text-ctc-blue px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Scale size={16} />
              <span>Legal Terms</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Terms of Service
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Terms and conditions governing your use of CTC Waterworks services
            </p>
            <div className="text-sm text-gray-500">
              Last updated: December 2024
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            
            {/* Acceptance of Terms */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-ctc-blue to-ctc-blue-dark rounded-lg flex items-center justify-center">
                  <FileText className="text-white" size={20} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 m-0">Acceptance of Terms</h2>
              </div>
              <div className="bg-gradient-to-br from-ctc-blue/5 to-ctc-blue/10 rounded-xl p-6 border border-ctc-blue/20">
                <p className="text-gray-700 leading-relaxed m-0">
                  Welcome to CTC Waterworks. These Terms of Service ("Terms") govern your use of our website, products, and services. By accessing our website, purchasing our products, or using our services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our website or services.
                </p>
              </div>
            </motion.div>

            {/* Company Information */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Company Information</h2>
              
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">CTC Waterworks</h3>
                    <div className="space-y-2 text-gray-700">
                      <div className="flex items-center space-x-2">
                        <MapPin size={16} className="text-ctc-blue" />
                        <span>460 McLaughlin Rd, Yakima, WA 98908</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Phone size={16} className="text-ctc-blue" />
                        <span>(509) 901-7476</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Mail size={16} className="text-ctc-blue" />
                        <span>Sales@CTCWaterworks.com</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Business Description</h4>
                    <p className="text-gray-700 text-sm">
                      CTC Waterworks is a leading provider of agricultural water solutions, specializing in sulfur burners, hydrocoolers, DPA drenchers, and irrigation filtration systems for commercial farming operations.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Products and Services */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                  <CheckCircle className="text-white" size={20} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 m-0">Products and Services</h2>
              </div>
              
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Product Offerings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Equipment</h4>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                        <li>Sulfur burners and pH management systems</li>
                        <li>Hydrocooling systems for post-harvest cooling</li>
                        <li>DPA drenchers for crop treatment</li>
                        <li>Irrigation filtration systems</li>
                        <li>Custom-engineered water solutions</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Services</h4>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                        <li>System design and engineering</li>
                        <li>Installation and commissioning</li>
                        <li>Maintenance and repair services</li>
                        <li>Technical support and training</li>
                        <li>Water quality analysis and consultation</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-1" size={20} />
                    <div>
                      <h4 className="font-semibold text-yellow-800 mb-2">Product Specifications</h4>
                      <p className="text-yellow-700 text-sm">
                        All product specifications, features, and capabilities are subject to change without notice. We reserve the right to modify or discontinue products at any time. Custom solutions are designed based on specific customer requirements and site conditions.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Orders and Payments */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Orders and Payments</h2>
              
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Order Process</h3>
                  <div className="space-y-4">
                    <div className="border-l-4 border-ctc-blue pl-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Quotations</h4>
                      <p className="text-gray-700 text-sm">All quotations are valid for 30 days unless otherwise specified. Prices are subject to change based on material costs, specifications, and delivery requirements.</p>
                    </div>
                    <div className="border-l-4 border-green-500 pl-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Order Acceptance</h4>
                      <p className="text-gray-700 text-sm">Orders are subject to acceptance by CTC Waterworks. We reserve the right to refuse or cancel orders at our discretion.</p>
                    </div>
                    <div className="border-l-4 border-orange-500 pl-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Custom Products</h4>
                      <p className="text-gray-700 text-sm">Custom-engineered products require detailed specifications and may have extended lead times. Changes to custom orders may incur additional charges.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Payment Terms</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Standard Terms</h4>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                        <li>Net 30 days for approved commercial accounts</li>
                        <li>Payment due upon delivery for new customers</li>
                        <li>Credit applications subject to approval</li>
                        <li>Late payment charges may apply</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Accepted Methods</h4>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                        <li>Check or money order</li>
                        <li>Wire transfer</li>
                        <li>Credit card (with processing fee)</li>
                        <li>Financing options available</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Delivery and Installation */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Delivery and Installation</h2>
              
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Delivery Terms</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Shipping</h4>
                        <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                          <li>FOB shipping point unless otherwise specified</li>
                          <li>Customer responsible for freight charges</li>
                          <li>Delivery dates are estimates, not guarantees</li>
                          <li>Customer must inspect goods upon delivery</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Risk of Loss</h4>
                        <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                          <li>Risk transfers to customer upon shipment</li>
                          <li>Customer responsible for insurance in transit</li>
                          <li>Damage claims must be filed with carrier</li>
                          <li>CTC Waterworks will assist with claim process</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Installation Services</h3>
                    <div className="bg-ctc-blue/5 rounded-lg p-4 border border-ctc-blue/20">
                      <p className="text-gray-700 text-sm mb-3">
                        Installation services are available for most products and are subject to separate terms and conditions:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                        <li>Site preparation is customer's responsibility</li>
                        <li>Installation scheduling subject to availability</li>
                        <li>Additional charges may apply for site-specific requirements</li>
                        <li>Customer must provide safe working conditions</li>
                        <li>Installation warranty separate from product warranty</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Warranties and Disclaimers */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                  <Shield className="text-white" size={20} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 m-0">Warranties and Disclaimers</h2>
              </div>
              
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Product Warranties</h3>
                  <div className="space-y-4">
                    <div className="border-l-4 border-green-500 pl-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Limited Warranty</h4>
                      <p className="text-gray-700 text-sm">CTC Waterworks warrants its products to be free from defects in materials and workmanship under normal use and service for the period specified in the product documentation.</p>
                    </div>
                    <div className="border-l-4 border-blue-500 pl-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Warranty Coverage</h4>
                      <p className="text-gray-700 text-sm">Warranty covers repair or replacement of defective parts. Customer is responsible for labor costs unless covered under separate service agreement.</p>
                    </div>
                    <div className="border-l-4 border-orange-500 pl-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Warranty Exclusions</h4>
                      <p className="text-gray-700 text-sm">Warranty does not cover damage from misuse, normal wear, unauthorized modifications, or failure to follow operating instructions.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 rounded-lg p-6 border border-red-200">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="text-red-600 flex-shrink-0 mt-1" size={20} />
                    <div>
                      <h4 className="font-semibold text-red-800 mb-2">Disclaimer of Warranties</h4>
                      <p className="text-red-700 text-sm mb-3">
                        EXCEPT AS EXPRESSLY SET FORTH HEREIN, CTC WATERWORKS DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE.
                      </p>
                      <p className="text-red-700 text-sm">
                        CTC Waterworks does not warrant that products will meet customer's specific requirements or that operation will be uninterrupted or error-free.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Limitation of Liability */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Limitation of Liability</h2>
              
              <div className="bg-red-50 rounded-lg p-6 border border-red-200">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-red-800 mb-3">Liability Limitations</h3>
                    <p className="text-red-700 text-sm mb-3">
                      IN NO EVENT SHALL CTC WATERWORKS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, LOSS OF USE, LOSS OF DATA, OR BUSINESS INTERRUPTION.
                    </p>
                    <p className="text-red-700 text-sm">
                      CTC Waterworks' total liability for any claim shall not exceed the purchase price of the product or service giving rise to the claim.
                    </p>
                  </div>
                  
                  <div className="border-t border-red-200 pt-4">
                    <h4 className="font-semibold text-red-800 mb-2">Agricultural Applications</h4>
                    <p className="text-red-700 text-sm">
                      Customer acknowledges that agricultural applications involve inherent risks and variables beyond CTC Waterworks' control. CTC Waterworks is not responsible for crop loss, yield reduction, or other agricultural damages.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Intellectual Property */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Intellectual Property</h2>
              
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Ownership</h3>
                    <p className="text-gray-700 text-sm">
                      All intellectual property rights in CTC Waterworks products, including but not limited to patents, trademarks, copyrights, and trade secrets, remain the exclusive property of CTC Waterworks.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">License</h3>
                    <p className="text-gray-700 text-sm">
                      Purchase of products grants customer a non-exclusive, non-transferable license to use the products in accordance with their intended purpose and operating instructions.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Restrictions</h3>
                    <p className="text-gray-700 text-sm">
                      Customer may not reverse engineer, disassemble, or attempt to derive the source code or design of any CTC Waterworks products without written permission.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Governing Law */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Governing Law and Disputes</h2>
              
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Governing Law</h3>
                    <p className="text-gray-700 text-sm">
                      These Terms shall be governed by and construed in accordance with the laws of the State of Washington, without regard to its conflict of law provisions.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Jurisdiction</h3>
                    <p className="text-gray-700 text-sm">
                      Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in Yakima County, Washington.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Dispute Resolution</h3>
                    <p className="text-gray-700 text-sm">
                      Before initiating legal proceedings, parties agree to attempt resolution through good faith negotiation. If unsuccessful, disputes may be submitted to binding arbitration.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-ctc-blue to-ctc-blue-dark rounded-lg flex items-center justify-center">
                  <Mail className="text-white" size={20} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 m-0">Contact Information</h2>
              </div>
              
              <div className="bg-gradient-to-br from-ctc-blue/5 to-ctc-blue/10 rounded-xl p-6 border border-ctc-blue/20">
                <p className="text-gray-700 mb-4">
                  For questions about these Terms of Service or our products and services, please contact us:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">CTC Waterworks</h4>
                    <div className="space-y-2 text-gray-700">
                      <div className="flex items-center space-x-2">
                        <MapPin size={16} className="text-ctc-blue" />
                        <span>460 McLaughlin Rd, Yakima, WA 98908</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Phone size={16} className="text-ctc-blue" />
                        <span>(509) 901-7476</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Mail size={16} className="text-ctc-blue" />
                        <span>Sales@CTCWaterworks.com</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Business Hours</h4>
                    <div className="space-y-1 text-gray-700 text-sm">
                      <div>Monday - Friday: 8:00 AM - 5:00 PM PST</div>
                      <div>Saturday: By appointment</div>
                      <div>Sunday: Closed</div>
                      <div className="mt-2 text-xs text-gray-500">
                        Emergency service available for existing customers
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Modifications */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Modifications to Terms</h2>
              
              <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
                <p className="text-gray-700 mb-3">
                  CTC Waterworks reserves the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting on our website. Your continued use of our products or services after any changes constitutes acceptance of the modified terms.
                </p>
                <p className="text-gray-700 text-sm">
                  We recommend reviewing these Terms periodically to stay informed of any updates. Material changes will be communicated to customers through appropriate channels.
                </p>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;